import parse, { attributesToProps, domToReact, Element, Text } from 'html-react-parser';
import type { DOMNode, HTMLReactParserOptions } from 'html-react-parser';
import { createElement, useMemo } from 'react';

function options(nodes: DOMNode[]): Element[] {
  return nodes.flatMap(node => !(node instanceof Element) ? [] : node.name === 'option' ? [node] : node.name === 'optgroup' ? options(node.children as DOMNode[]) : []);
}

function text(nodes: DOMNode[]): string {
  return nodes.map(node => node instanceof Text ? node.data : node instanceof Element ? text(node.children as DOMNode[]) : '').join('');
}

// Templates mark the chosen <option selected>; React wants defaultValue on the <select> instead.
const parserOptions: HTMLReactParserOptions = {
  replace(node) {
    if (!(node instanceof Element)) return;
    // Template forms read their fields in mount hooks, so React must not reset edits.
    if (node.name === 'input' && 'value' in node.attribs) {
      const { value, ...props } = attributesToProps(node.attribs);
      return createElement('input', { ...props, defaultValue: value });
    }
    if (node.name !== 'select') return;
    const chosen = options(node.children as DOMNode[]).filter(option => 'selected' in option.attribs);
    if (!chosen.length) return;
    const values = chosen.map(option => option.attribs.value ?? text(option.children as DOMNode[]));
    for (const option of chosen) delete option.attribs.selected;
    const props = { ...attributesToProps(node.attribs), defaultValue: 'multiple' in node.attribs ? values : values[values.length - 1] };
    return createElement('select', props, domToReact(node.children as DOMNode[], parserOptions));
  },
};

/** Escaped template markup from the existing screen renderers, reconciled by React. */
export function Markup({ value }: { value: { toString(): string } | string }) {
  const source = String(value ?? '');
  return useMemo(() => parse(source, parserOptions), [source]);
}
